// @ts-nocheck
import * as _ from 'lodash';
import {
  SlickPlugin,
  SlickGridModel,
  Column,
  SlickEventHandler,
  SlickEvent,
  SlickEventData,
  OnColumnsResizedEventArgs,
  OnColumnsReorderedEventArgs,
} from 'slickgrid';

interface StateOptions<T> {
  key: string;
  defaultColumns: Column<T>[];
}

interface ColumnState {
  columnId: string;
  width: number;
}

interface StateData {
  sortcols: { columnId: string; sortAsc: boolean }[];
  viewport: Dictionary<any>;
  columns: ColumnState[];
  knownColumns: string[];
}

interface StateChangedEventData extends StateData {}

function getItem(key: string) {
  const data = localStorage.getItem(key);

  if (data) {
    return JSON.parse(data);
  }
}

function setItem(key: string, value: StateData) {
  localStorage.setItem(key, JSON.stringify(value));
}

function removeItem(key: string) {
  localStorage.removeItem(key);
}

export class State<T> implements SlickPlugin {
  pluginName = 'State' as const;

  public static __name__ = 'State';

  private readonly eventHandler: SlickEventHandler;
  private readonly options: StateOptions<T>;
  private grid: SlickGridModel<T>;

  readonly key: string;
  readonly isDisabled: boolean = false;
  readonly onStateChanged: SlickEvent<StateChangedEventData>;

  constructor(options: StateOptions<T>) {
    this.eventHandler = new SlickEventHandler();
    this.options = options;

    if (!window.localStorage) {
      console.warn(
        'LocalStorage is not available.' +
          'Slickgrid statepersistor plugin will be disabled disabled.',
      );
      this.isDisabled = true;
    } else {
      this.onStateChanged = new SlickEvent();
      this.key = options.key;
    }
  }

  public init(grid: SlickGridModel): void {
    if (this.isDisabled) {
      return;
    }

    this.grid = grid;

    this.eventHandler
      .subscribe(grid.onColumnsResized, this.onColumnsResized.bind(this))
      .subscribe(grid.onColumnsReordered, this.onColumnsReordered.bind(this))
      .subscribe(grid.onSort, this.onSort.bind(this));

    setTimeout(() => {
      this.restore();
    }, 50);
  }

  public destroy(): void {
    if (this.isDisabled) {
      return;
    }

    this.eventHandler.unsubscribeAll();
    this.save();
  }

  /**
   * The state is empty, nothing has been persisted so far.
   */
  get isEmpty() {
    return !getItem(this.key);
  }

  public save() {
    const state: StateData = {
      sortcols: _.map(this.grid.getSortColumns(), (p) => ({
        columnId: p.columnId,
        sortAsc: p.sortAsc,
      })),
      viewport: this.grid.getViewport(),
      columns: _.map(this.grid.getColumns(), (col) => ({
        columnId: col.id,
        width: col.width,
      })),
      knownColumns: _.map(this.options.defaultColumns, (p) => p.id),
    };

    setItem(this.key, state);

    this.onStateChanged.notify(state);
  }

  public restore() {
    const state = getItem(this.key);
    if (!state) {
      return;
    }

    if (state.sortcols) {
      this.grid.setSortColumns(state.sortcols);
    }

    if (state.viewport) {
      this.grid.scrollRowIntoView(state.viewport.top, true);
    }

    if (state.columns && this.options.defaultColumns) {
      this.restoreColumns(state, this.options.defaultColumns);
    }
  }

  private restoreColumns(state: StateData, defaults: Column<T>[]) {
    const knownColumns = state.knownColumns || [],
      statesIdentities = _.map(state.columns, (p) => p.columnId),
      defsById: Dictionary<Column<T>> = _.reduce(
        defaults,
        (m, col) => ((m[col.id] = col), m),
        {},
      );

    // first display frozen and unknown (new) columns
    const cols: Column<T>[] = _.filter(defaults, (p) => {
      const hidden = statesIdentities.indexOf(p.id) > -1;

      if (p['frozen'] && hidden) {
        return true;
      }
    });

    const firstSystemIndex = cols.length;

    const visited = _.map(cols, (p) => p.id);

    _.each(state.columns, (state) => {
      if (visited.indexOf(state.columnId) > -1) {
        return;
      }

      visited.push(state.columnId);

      const column = defsById[state.columnId];
      if (!column) {
        return;
      }

      cols.push({ ...column, width: state.width });
    });

    // insert new (unkown) items after the position they originally belong to.
    _.each(defaults, (col, index, arr) => {
      if (knownColumns.indexOf(col.id) !== -1) {
        return;
      }

      if (index === 0) {
        cols.splice(firstSystemIndex + 1, 0, col);
        return;
      }

      const prev = arr[index - 1];
      const prevIndex = _.findIndex(cols, { id: prev.id });

      if (prevIndex > -1) {
        cols.splice(prevIndex + 1, 0, col);
        return;
      }

      cols.splice(firstSystemIndex + 1, 0, col);
    });

    for (const col of cols) {
      if (col.width == null) {
        col.width = 50;
      }
    }

    this.grid.setColumns(cols);
  }

  private onColumnsResized(e: SlickEventData, args: OnColumnsResizedEventArgs) {
    try {
      this.save();
    } catch (err) {
      console.error(err);
    }
  }

  private onColumnsReordered(
    e: Slick.EventData,
    args: OnColumnsReorderedEventArgs,
  ) {
    try {
      this.save();
    } catch (err) {
      console.error(err);
    }
  }

  private onSort(e: Slick.EventData, args: OnColumnsResizedEventArgs) {
    try {
      this.save();
    } catch (err) {
      console.error(err);
    }
  }
}
